import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { FiPlay, FiExternalLink, FiInstagram, FiMusic } from 'react-icons/fi'
import { usePublicArtists } from '../hooks/useArtists'
import { usePublicContent } from '../hooks/usePublicContent'
import { ArtistsContent } from '../types/content'
import VideoThumbnail from './VideoThumbnail'
import VideoModal from './VideoModal'

const Artists: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  // Get CMS content for Artists section
  const { content } = usePublicContent('artists')
  const artistsData = content?.data as ArtistsContent

  // Video modal state
  const [selectedVideo, setSelectedVideo] = useState<{
    videoUrl: string
    title: string
    artist: string
  } | null>(null)

  // Fetch artists from Firebase
  const { artists: allArtists, loading } = usePublicArtists()
  
  // Filter only active artists
  const artists = allArtists.filter(artist => artist.active)

  const featuredArtists = artists.filter(artist => artist.featured)
  const regularArtists = artists.filter(artist => !artist.featured)

  const handleVideoClick = (videoUrl: string, title: string, artist: string) => {
    setSelectedVideo({ videoUrl, title, artist })
  }

  const closeVideoModal = () => {
    setSelectedVideo(null)
  }
  
  // Show loading state
  if (loading) {
    return (
      <section id="artists" className="section-padding bg-white">
        <div className="container-custom text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading artists...</p>
        </div>
      </section>
    )
  }

  return (
    <section id="artists" className="section-padding bg-white">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16 px-4"
        >
          <h2 className="font-bold text-neutral-800 mb-4 sm:mb-6" style={{ fontSize: 'clamp(28px, 4vw, 48px)' }}>
            Available <span className="gradient-text">Musicians</span>
          </h2>
          <p className="body-longform text-neutral-600 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
            Browse our curated roster of professional musicians available for your events. 
            From acoustic soloists to full bands, we have the perfect performers for any occasion.
          </p>
        </motion.div>

        {/* Featured Artists - Large Cards */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-12 sm:mb-16"
        >
          <h3 className="font-bold text-neutral-800 mb-6 sm:mb-8 text-center px-4" style={{ fontSize: 'clamp(22px, 3vw, 32px)' }}>Featured Performers</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {featuredArtists.map((artist, index) => (
              <motion.div
                key={artist.id}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group relative bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500"
              >
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${artist.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                
                {/* Media - Video or Image */}
                <div className="relative h-64 sm:h-72 md:h-80 overflow-hidden">
                  {artist.videoUrl ? (
                    <VideoThumbnail
                      videoUrl={artist.videoUrl}
                      className="w-full h-full"
                      autoPlay={true}
                      loop={true}
                      muted={true}
                    />
                  ) : (
                    <img
                      src={artist.image}
                      alt={artist.name}
                      className="w-full h-full object-cover object-[center_30%] transition-transform duration-500 group-hover:scale-110"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  
                  {/* Genre Badge */}
                  <div className="absolute top-3 sm:top-4 left-3 sm:left-4">
                    <span className="px-2.5 sm:px-3 py-1 bg-white/90 backdrop-blur-sm text-neutral-800 text-xs sm:text-sm font-medium rounded-full">
                      {artist.genre}
                    </span>
                  </div>

                  {/* Play Button - Show only for videos */}
                  {artist.videoUrl && (
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      whileHover={{ scale: 1.1 }}
                      className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      onClick={() => handleVideoClick(artist.videoUrl!, artist.name, artist.genre)}
                    >
                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-primary rounded-full flex items-center justify-center shadow-lg cursor-pointer">
                        <FiPlay className="text-white text-lg sm:text-xl ml-1" />
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Content */}
                <div className="relative p-4 sm:p-6">
                  <h4 className="text-lg sm:text-xl font-bold text-neutral-800 mb-2">{artist.name}</h4>
                  <p className="body-longform text-neutral-600 mb-3 sm:mb-4 text-sm sm:text-base line-clamp-3">{artist.description}</p>
                  

                  {/* Social Links */}
                  <div className="flex space-x-2 sm:space-x-3">
                    {artist.socialLinks?.instagram && (
                      <motion.a
                        href={artist.socialLinks.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 bg-neutral-100 hover:bg-neutral-200 rounded-xl text-neutral-600 hover:text-primary transition-colors duration-200 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        <FiInstagram size={16} />
                      </motion.a>
                    )}
                    {artist.socialLinks?.spotify && (
                      <motion.a
                        href={artist.socialLinks.spotify}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 bg-neutral-100 hover:bg-neutral-200 rounded-xl text-neutral-600 hover:text-primary transition-colors duration-200 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        <FiMusic size={16} />
                      </motion.a>
                    )}
                    {artist.socialLinks?.website && (
                      <motion.a
                        href={artist.socialLinks.website.startsWith('http') ? artist.socialLinks.website : `https://${artist.socialLinks.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 bg-neutral-100 hover:bg-neutral-200 rounded-xl text-neutral-600 hover:text-primary transition-colors duration-200 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        <FiExternalLink size={16} />
                      </motion.a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Regular Artists - Compact Cards */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-12 sm:mb-16"
        >
          <h3 className="font-bold text-neutral-800 mb-6 sm:mb-8 text-center px-4" style={{ fontSize: 'clamp(22px, 3vw, 32px)' }}>More Performers</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {regularArtists.map((artist, index) => (
              <motion.div
                key={artist.id}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group card-hover overflow-hidden"
              >
                <div className="flex">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0">
                    <img
                      src={artist.image}
                      alt={artist.name}
                      className="w-full h-full object-cover object-[center_30%]"
                    />
                  </div>
                  <div className="flex-1 p-3 sm:p-4">
                    <h5 className="font-semibold text-neutral-800 mb-1 text-sm sm:text-base">{artist.name}</h5>
                    <p className="text-primary text-xs sm:text-sm mb-2">{artist.genre}</p>
                    <div className="flex items-center space-x-3 sm:space-x-4 text-xs text-neutral-500">
                      <span>{artist.stats.streams}</span>
                      <span>{artist.stats.followers}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center bg-gradient-to-r from-primary to-secondary rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-white"
        >
          <h3 className="font-bold mb-3 sm:mb-4" style={{ fontSize: 'clamp(20px, 3vw, 32px)' }}>
            {artistsData?.ctaTitle || 'Ready to Book Live Music?'}
          </h3>
          <p className="body-longform mb-6 sm:mb-8 max-w-2xl mx-auto px-2 text-sm sm:text-base leading-relaxed">
            {artistsData?.ctaDescription || 'Contact us to discuss your event needs and we\'ll help you find the perfect musicians to make your occasion truly special.'}
          </p>
          <div className="flex items-center justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-primary font-semibold py-3 px-6 sm:px-8 rounded-2xl hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary transition-all duration-200"
            >
              {artistsData?.ctaButtonText || 'Get in Touch'}
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <VideoModal
          isOpen={!!selectedVideo}
          onClose={closeVideoModal}
          videoUrl={selectedVideo.videoUrl}
          title={selectedVideo.title}
          artist={selectedVideo.artist}
        />
      )}
    </section>
  )
}

export default Artists